import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Datadog } from "@/utils/observability/datadog";
import useInfoLogger from "@/utils/useInfoLogger";
import AdditionalInfo from "../additionalInfo/additionalInfo";
import CopyText from "../copyText/copyText";

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

/**
 * Tracks whether the app is installed as PWA (Progressive Web Application)
 * See: https://web.dev/explore/progressive-web-apps
 */
export default function PwaDetector() {
  const [isInPWA, setIsInPWA] = useState(false);
  const logInfo = useInfoLogger();

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined")
      return;

    const checkPWA = () => {
      // Check if running in standalone mode
      const isStandalone = window.matchMedia(
        "(display-mode: standalone)"
      ).matches;

      // Check if installed as PWA (iOS)
      const isIOSStandalone =
        (window.navigator as NavigatorWithStandalone).standalone === true;

      // Check if running in PWA context (Android)
      const isInPWA =
        isStandalone ||
        isIOSStandalone ||
        window.matchMedia("(display-mode: minimal-ui)").matches;

      setIsInPWA(isInPWA);
    };

    // Initial check
    checkPWA();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    mediaQuery.addEventListener("change", checkPWA);

    return () => {
      mediaQuery.removeEventListener("change", checkPWA);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isInPWA) {
      logInfo("PWA Mode Detected");
    }
    Datadog.setIsPWA(isInPWA);
  }, [isInPWA, logInfo]);

  if (!isInPWA) return null;

  return (
    <AdditionalInfo
      variant="warning"
      text={
        <Box pr={2}>
          <Typography variant="body2">
            Moxie Suite doesn&apos;t work properly when opened from the app icon
            you saved to your home screen.
          </Typography>
          <Typography variant="body2">
            <strong>
              Copy and paste the URL into a regular browser tab instead.
            </strong>
          </Typography>
          <div style={{ marginTop: "0.75rem" }}>
            <CopyText textToCopy={window.location.href} />
          </div>
        </Box>
      }
      sx={{
        position: "sticky",
        insetInline: 0,
        top: 0,
        zIndex: 10000,
        mt: 0,
        borderRadius: 0,
        justifyContent: "center",
        py: 2,
      }}
    />
  );
}
